import { Text } from "@ttc3k/trekker";
import { Box, Divider, Flex } from "styled-system/jsx";
import {
  formatPrice,
  getTaxLineLabel,
  resolveTaxLinesForDisplay,
  type TaxLineDisplay,
} from "shared/utils";

export type PricingSummaryItem = {
  label: string;
  amount: number;
  /** Secondary line under the label (e.g. membership coverage dates). */
  detail?: string;
};

export type PricingSummaryLabels = {
  subtotal: string;
  /** Localized generic "Tax" used for unnamed tax lines. */
  tax: string;
  fee: string;
  total: string;
};

type PricingSummaryProps = {
  currency?: string | null;
  /** Line items (cart lines + additional items) rendered above the subtotal. */
  items?: PricingSummaryItem[];
  /** Pre-discount, pre-tax subtotal. */
  subtotal: number;
  discount?: { label: string; amount: number } | null;
  taxLines?: TaxLineDisplay[] | null;
  /** Lumped tax total; used as a single-line fallback when `taxLines` is empty. */
  taxTotal: number;
  /**
   * Customer application fee included in `total`. Pass `null`/`undefined`
   * for legacy records where the fee was never stored (row is hidden).
   */
  customerFee?: number | null;
  total: number;
  labels: PricingSummaryLabels;
  /** "invoice": light labels + dividers (public pages/PDF). "panel": compact admin box. */
  visual?: "invoice" | "panel";
};

const formatAmount = (cents: number) => `$${formatPrice(cents)}`;

/**
 * Canonical pricing totals block: subtotal, discount, one row per tax line
 * (e.g. GST + PST), customer fee, and total. Shared by the public invoice
 * page/PDF, the storefront invoice detail view, and the admin invoice
 * composer so every surface presents the same breakdown.
 */
export const PricingSummary = ({
  currency: _currency,
  items,
  subtotal,
  discount,
  taxLines,
  taxTotal,
  customerFee,
  total,
  labels,
  visual = "invoice",
}: PricingSummaryProps) => {
  const isInvoice = visual === "invoice";
  const labelColor = isInvoice ? "text.light" : "text.mid";
  const labelVisual = isInvoice ? "smallMedium" : "bodyRegular";
  const amountVisual = isInvoice ? "smallRegular" : "bodyRegular";
  const amountProps = isInvoice ? {} : { fontFamily: "mono" as const };
  const displayTaxLines = resolveTaxLinesForDisplay(taxLines, taxTotal);

  const row = (
    key: string,
    label: string,
    amount: string,
    options?: { color?: string; detail?: string },
  ) => (
    <Flex
      key={key}
      justifyContent="space-between"
      alignItems="baseline"
      gap="200"
    >
      <Flex flexDir="column" flex="1" minW="0" gap="25">
        <Text visual={labelVisual} color={options?.color ?? labelColor}>
          {label}
        </Text>
        {options?.detail ? (
          <Text visual="smallRegular" color="text.mid">
            {options.detail}
          </Text>
        ) : null}
      </Flex>
      <Text
        visual={amountVisual}
        color={options?.color ?? "text.dark"}
        flexShrink={0}
        {...amountProps}
      >
        {amount}
      </Text>
    </Flex>
  );

  return (
    // breakInside keeps the totals block on one page in Playwright-printed
    // PDFs (browsers still split it if it exceeds a full page).
    <Flex
      flexDir="column"
      gap={isInvoice ? "150" : "100"}
      css={{ breakInside: "avoid", pageBreakInside: "avoid" }}
    >
      {items && items.length > 0 && (
        <>
          {items.map((item, idx) =>
            row(`item-${idx}`, item.label, formatAmount(item.amount), {
              detail: item.detail,
            }),
          )}
          {isInvoice ? (
            <Divider borderColor="border.light" />
          ) : (
            <Box borderTopWidth="1px" borderColor="border.light" my="50" />
          )}
        </>
      )}
      {row("subtotal", labels.subtotal, formatAmount(subtotal))}
      {discount && discount.amount > 0
        ? row(
            "discount",
            discount.label,
            `-${formatAmount(discount.amount)}`,
            { color: "text.success.dark" },
          )
        : null}
      {displayTaxLines.map((line, idx) =>
        row(
          `tax-${idx}`,
          getTaxLineLabel(line, labels.tax),
          formatAmount(line.amount),
        ),
      )}
      {customerFee != null && customerFee > 0
        ? row("fee", labels.fee, formatAmount(customerFee))
        : null}
      {isInvoice ? (
        <Divider borderColor="border.light" />
      ) : (
        <Box borderTopWidth="1px" borderColor="border.light" my="50" />
      )}
      <Flex justifyContent="space-between" alignItems="baseline" gap="200">
        <Text
          visual="bodySemiBold"
          color="text.dark"
          flex="1"
          minW="0"
        >
          {labels.total}
        </Text>
        <Text
          visual={isInvoice ? "h5" : "bodySemiBold"}
          color="text.dark"
          flexShrink={0}
          {...amountProps}
        >
          {formatAmount(total)}
        </Text>
      </Flex>
    </Flex>
  );
};
